import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { url } from "../constants/url";

const AddCompanyForNewUser = ({ email }) => {
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [superAdminId, setSuperAdminId] = useState(null);

  useEffect(() => {
    const fetchSuperAdmin = async () => {
      try {
        const response = await fetch(`${url}/api/users/super-admin`);
        
        if (response.ok) {
          const superAdmin = await response.json();
          setSuperAdminId(superAdmin._id);
        } else {
          const fallbackResponse = await fetch(`${url}/api/users/by-role/superAdmin`);
          if (fallbackResponse.ok) {
            const superAdmins = await fallbackResponse.json();
            if (superAdmins.length > 0) {
              setSuperAdminId(superAdmins[0]._id);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching super admin:", error);
      }
    };

    fetchSuperAdmin();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!companyName.trim()) {
      setError("Please enter a company name");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${url}/api/admin/add-company`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name: companyName }),
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to submit company");
      }
      
      await res.json();
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      console.error("Submission error:", err);
    }
    
    setLoading(false);
  };

  if (submitted) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="body1"
          color="success.main"
          fontWeight="bold"
          gutterBottom
        >
          ✓ Submitted Successfully!
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
        >
          Your company "{companyName}" is pending approval.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
      <TextField
        label="Company Name"
        value={companyName}
        onChange={(e) => {
          setCompanyName(e.target.value);
          setError("");
        }}
        fullWidth
        required
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
          },
        }}
        placeholder="Enter company name"
        disabled={loading}
        size="small"
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading || !companyName.trim()}
        sx={{
          py: 1,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.9rem",
          bgcolor: "success.main",
          "&:hover": {
            bgcolor: "success.dark",
            boxShadow: 3,
            transform: "translateY(-1px)",
          },
          "&:disabled": {
            bgcolor: "grey.300",
          },
          transition: "all 0.2s ease-in-out",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} sx={{ color: "white" }} />
            Submitting...
          </Box>
        ) : (
          "Submit Company"
        )}
      </Button>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mt: 2, 
            borderRadius: 1,
            fontSize: '0.8rem',
            py: 0.5
          }}
        >
          {error}
        </Alert>
      )}

      {!superAdminId && (
        <Alert 
          severity="warning" 
          sx={{ 
            mt: 2, 
            borderRadius: 1,
            fontSize: '0.7rem',
            py: 0.5
          }}
        >
          Admin notification might be delayed
        </Alert>
      )}
    </Box>
  );
};

export default AddCompanyForNewUser;